import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild, inject } from "@angular/core";
import { ChartJsContainerComponent, rgb } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService, NavigationService, UrlService, } from "@tylertech/core-module";
import { ICustomViewComponent, } from "@tylertech/custom-view";
import { IntentService } from "@tylertech/intents-module";
import { AppLinkParms, ExecutionResult, FilterItem, IChartCriteria, ProfileSettingViewModel, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfigurationCustomTypesPerDataset, ChartEvent, ChartType, Color, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SegmentService } from "../../services";

@Component({
    selector: "general-ledger-monthly-revenue-chart",
    templateUrl: "./monthlyRevenueChart.tmpl.html",
    styleUrls: ["./monthlyRevenueChart.component.scss"],
    standalone: false
})
export class MonthlyRevenueChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $loggingService = inject(LoggingService);
    private readonly $fileService = inject(FileService);
    private readonly $urlService = inject(UrlService);
    private readonly $segmentService = inject(SegmentService);
    private readonly $intentService = inject(IntentService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    chartConfig: ChartConfigurationCustomTypesPerDataset;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;
    isLoaded: boolean;
    ChartTitle: string = "Monthly Revenue";
    annualRevenueBcfId: string;
    annualRevenueFundIds: string[];
    readonly showChart = signal(false);
    private chartDataColors: ReadonlyArray<Color>;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        let ret = "";
        const struct = this.chartCriteria?.Parameters?.reduce((v, x) =>
        {
            switch (x.Id)
            {
                case "RevenueFiscal1":
                    v.fiscalId1 = x;
                    break;
                case "RevenueFiscal2":
                    v.fiscalId2 = x;
                    break;
                case "RevenueFundIds":
                    v.funds = x;
                    break;
                case "RevenueGroupById":
                    v.groupBy = x;
                    break;
            }
            return v;
        }, {} as Record<"fiscalId1" | "fiscalId2" | "funds" | "groupBy", ProfileSettingViewModel | undefined>);

        if (!struct)
            return ret;

        const { fiscalId1, fiscalId2, funds, groupBy } = struct;

        if (fiscalId1?.Value)
        {
            const fiscalId1Desc = fiscalId1.Options.find(x => x.Value === fiscalId1.Value);
            if (fiscalId1Desc)
            {
                ret = ret + fiscalId1Desc.Display;
            }
        }
        if (fiscalId2?.Value)
        {
            const fiscalId2Desc = fiscalId2.Options.find(x => x.Value === fiscalId2.Value);
            if (fiscalId2Desc)
            {
                ret = ret + " & " + fiscalId2Desc.Display;
            }
        }
        if (funds?.Value)
        {
            ret = ret + " &bull; By Funds";
        }
        if (groupBy?.Value)
        {
            const groupByDesc = groupBy.Options.find(x => x.Value === groupBy.Value);
            if (groupByDesc)
            {
                ret = ret + " &bull; By " + groupByDesc.Display;
            }
        }
        return ret;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.chartDataColors = this.$chartService.getColors(3);

        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private getParameterById(parameterId: string): ProfileSettingViewModel | undefined
    {
        const parameters = this.chartCriteria?.Parameters?.find(g => g.Id === parameterId);
        return parameters ? parameters[0] : undefined;
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0 && !!this.chartCriteria.ChartDatas[1];
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                const revenueGroupById = this.getParameterById("RevenueGroupById")?.Value;
                if (revenueGroupById)
                {
                    this.$segmentService.GetSubSegmentValues(revenueGroupById).then(result =>
                    {
                        const sub = this.getParameterById("RevenueGroupBySubId");
                        if (sub)
                        {
                            sub.Options = [
                                <SettingLookupOption>{
                                    Value: "",
                                    Display: "View All",
                                },
                                ...result.map(x =>
                                {
                                    return <SettingLookupOption>{
                                        Value: x.Id,
                                        Display: x.Name,
                                    };
                                }),
                            ];
                        }
                        sub.Value = "";
                    });
                }
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const trendLineColor = this.$chartService.getTrendLineColor();
        const numDatasets = this.chartCriteria.ChartDatas.length;
        this.chartConfig = {
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12 },
                        // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<ChartType>): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = !meta.hidden ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (!iMeta.hidden && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<ChartType>[]) =>
                            {
                                return this.chartCriteria.ChartDatas[0].Labels[tooltipItems[0].dataIndex];
                            },
                            label: (tooltipItem: TooltipItem<ChartType>) =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        },
                        borderWidth: 1
                    },
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (!element)
                    {
                        return;
                    }
                    const index = element.index;
                    const datasetIndex = element.datasetIndex;
                    const subset: ProfileSettingViewModel = this.chartCriteria?.Parameters?.find(
                        v => v.Id === "RevenueGroupBySubId"
                    );
                    const gr = this.chartCriteria?.Parameters?.find(v => v.Id === "RevenueGroupById");
                    const groupBy = gr?.Options.find(x => x.Value === gr.Value)?.Display;
                    let fiscalId: string;
                    let periodId: number;
                    if (datasetIndex === 0)
                    {
                        fiscalId = this.chartCriteria?.Parameters?.filter(x => x.Id === "RevenueFiscal2")[0].Value;
                        periodId = index + 1;
                    }
                    else if (datasetIndex === 1)
                    {
                        fiscalId = this.chartCriteria?.Parameters?.filter(x => x.Id === "RevenueFiscal1")[0].Value;
                        periodId = index + 1;
                    }
                    else
                    {
                        return;
                    }
                    const filter: FilterItem[] = [
                        {
                            Key: "BudgetCodeFiscalId",
                            Value: fiscalId,
                        },
                        {
                            Key: "PeriodId",
                            Value: `${periodId}`,
                        },
                        {
                            Key: "AccountType",
                            Value: "Revenue",
                        },
                        {
                            Key: "Account Status",
                            Value: "Active",
                        },
                        {
                            Key: "NonZeroActivity",
                            Value: "true",
                        },
                    ];
                    if (this.chartCriteria?.Parameters?.find(x => x.Id === "RevenueFundIds")?.Value)
                    {
                        filter.push({
                            Key: "FundIds",
                            Value: this.chartCriteria?.Parameters?.filter(x => x.Id === "RevenueFundIds")[0].Value,
                        });
                    }
                    if (subset.Value)
                    {
                        const queryString = subset.Value.split(",").reduce(
                            (c, n) => c + ` "${groupBy}"="${subset.Options.find(v => v.Value === n)?.Display}"`,
                            ""
                        );
                        filter.push({
                            Key: "QueryString",
                            Value: queryString,
                        });
                    }
                    const parms: AppLinkParms = {
                        App: "glaccounts",
                        Parms: filter,
                    };
                    this.$urlService.GetAppLink(parms).then((executionResult: ExecutionResult<string>) =>
                    {
                        const index = executionResult.Result.indexOf("?");
                        if (executionResult.Result.includes("apps/form"))
                        {
                            this.$navigationService.navigateToApp(executionResult.Result);
                        }
                        else if (index > 0)
                        {
                            this.$intentService.resolve("GLAccounts", "Search", {
                                querystring: executionResult.Result.substring(index, executionResult.Result.length),
                            });
                        }
                    });
                },
                elements: { line: {} },
                scales: {
                    x: {
                        type: "category",
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                        display: true,
                        grid: { display: true },
                    },
                    barAxis: {
                        type: "linear",
                        display: true,
                        position: "right",
                        title: {
                            display: true,
                            text: numDatasets === 2
                                ? this.chartCriteria.ChartDatas[0].SetLabel
                                : this.chartCriteria.ChartDatas[0].SetLabel + " / " + this.chartCriteria.ChartDatas[1].SetLabel,
                            font: { size: 18, },
                        },
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                    lineAxis: {
                        type: "linear",
                        display: true,
                        position: "left",
                        title: {
                            display: true,
                            text: numDatasets === 2
                                ? this.chartCriteria.ChartDatas[1].SetLabel
                                : this.chartCriteria.ChartDatas[2].SetLabel,
                            font: { size: 18, },
                        },
                        grid: { display: false },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
                datasets: {
                    bar: {
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                    line: {
                        tension: 0.5,
                        pointStyle: 'line',
                    },
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.length === 3
                    ? [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            yAxisID: "barAxis",
                            backgroundColor: this.chartDataColors[0],
                            borderColor: this.chartDataColors[0],
                            hoverBackgroundColor: this.chartDataColors[0],
                            hoverBorderColor: this.chartDataColors[0],
                        },
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            yAxisID: "barAxis",
                            backgroundColor: this.chartDataColors[1],
                            borderColor: this.chartDataColors[1],
                            hoverBackgroundColor: this.chartDataColors[1],
                            hoverBorderColor: this.chartDataColors[1],
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[2].SetLabel}`,
                            yAxisID: "lineAxis",
                            data: this.chartCriteria.ChartDatas[2].Data,
                            borderColor: trendLineColor,
                            backgroundColor: trendLineColor,
                            pointHoverBackgroundColor: trendLineColor,
                            pointHoverBorderColor: trendLineColor,
                        },
                    ]
                    : [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            yAxisID: "barAxis",
                            backgroundColor: this.chartDataColors[1],
                            borderColor: this.chartDataColors[1],
                            hoverBackgroundColor: this.chartDataColors[1],
                            hoverBorderColor: this.chartDataColors[1],
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            yAxisID: "lineAxis",
                            borderColor: trendLineColor,
                            backgroundColor: trendLineColor,
                            pointHoverBackgroundColor: trendLineColor,
                            pointHoverBorderColor: trendLineColor,
                        },
                    ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
